<template>
	<div
		class="article-contributors"
		:class="[classes]"
	>
		<span class="flex items-center gap-[8px]">
			<div class="contributor-profile-images inline-flex flex-wrap gap-2">
				<span class="flex contributor-profile-avatar">
					<img
						width="24"
						height="24"
						:src="filterAvatar"
						:alt="author.name + 'Avatar'"
					/>
				</span>
				<span
					v-if="reviewer && reviewer.avatar"
					class="flex contributor-profile-avatar"
				>
					<img
						width="24"
						height="24"
						:src="reviewer.avatar"
						:alt="reviewer.name + 'Avatar'"
					/>
				</span>
				<span
					v-if="factChecker && factChecker.avatar"
					class="flex contributor-profile-avatar"
				>
					<img
						width="24"
						height="24"
						:src="factChecker.avatar"
						:alt="factChecker.name + 'Avatar'"
					/>
				</span>
			</div>

			<span
				:class="[
					'text-base font-normal capitalize whitespace-nowrap',
					{
						'text-white': isAlternative
					}
				]"
			>
				Written by
				<NuxtLink
					:to="generateBioPageLink(author.name)"
					:class="{
						'!text-white': isAlternative
					}"
				>
					{{ author.name === "Tim Sykes" ? "Timothy Sykes" : author.name }}
				</NuxtLink>
			</span>
		</span>
		<span
			v-if="reviewer && reviewer.name"
			:class="[
				'byline text-base',
				{
					'text-white': isAlternative
				}
			]"
		>
			Reviewed by
			<NuxtLink
				:to="generateBioPageLink(reviewer.name)"
				:class="{
					'!text-white': isAlternative
				}"
			>
				{{ reviewer.name }}
			</NuxtLink>
		</span>
		<span
			v-if="factChecker && factChecker.name"
			:class="[
				'byline text-base',
				{
					'text-white': isAlternative
				}
			]"
		>
			Fact-checked by
			<NuxtLink
				:to="generateBioPageLink(factChecker.name)"
				:class="{ '!text-white': isAlternative }"
			>
				{{ factChecker.name }}
			</NuxtLink>
		</span>
	</div>
</template>

<script setup lang="ts">
type Authorship = {
	class?: string;
	isAlternative?: boolean;
	author: {
		avatar: string;
		name: string;
	};
	reviewer: {
		avatar: string;
		name: string;
	};
	factChecker: {
		avatar: string;
		name: string;
	};
};

const props = defineProps<Authorship>();

const classes = computed(() => props.class);

const generateBioPageLink = (name: string) => {
	let url = name.includes("-")
		? name.replace("-", " ").split(" ").join("-")
		: name.split(" ").join("-");

	url = url === "Timothy-Sykes" ? "tim-sykes" : url;

	const pages = [
		"bohen-bot",
		"ed-weinberg",
		"friedrich-odermann",
		"jordan-s",
		"stephanie-thompson",
		"tim-bohen",
		"tim-bot",
		"tim-sykes",
		"timothysykes",
		"ben-sturgill",
		"bryce-tuohey",
		"matt-monaco",
		"jeff-zananiri",
		"ellis-hobbs",
		"jack-kellogg"
	];

	if (!pages.includes(url.toLocaleLowerCase())) {
		url = "tim-sykes";
	}

	return `/blog/author/${url.toLowerCase()}/`;
};

const filterAvatar = computed(() => {
	const avatarMap = {
		"Timothy Sykes": props.author.avatar,
		"Tim-bot": "/images/authorsPics/timbot.webp",
		"Tim bot": "/images/authorsPics/timbot.webp",
		"Bryce Tuohey": "/images/authorsPics/bryce-tuohey.png",
		"Ellis Hobbs": "/images/authorsPics/ellis-hobbs.png",
		"Jack Kellogg": "/images/authorsPics/jack-kellogg.png"
	};

	return (
		avatarMap[props.author.name] ||
		props.author.avatar ||
		"https://secure.gravatar.com/avatar/092522e87ab76a4bb688d6bfdc0b4f2e?s=96&d=mm&r=g"
	);
});
</script>

<style lang="scss" scoped>
@import "@assets/utils.scss";

.article-contributors {
	display: flex;
	gap: 8px;
	color: $neutral-600;
	align-items: center;
	flex-wrap: wrap;
	a {
		text-decoration: underline;
		color: $primary-500;
	}

	@include breakpoint(md) {
		flex-wrap: nowrap;
	}

	.byline {
		display: block;
		width: 100%;
		padding-left: 32px;
		font-weight: 400;
		@include breakpoint(md) {
			display: inline-block !important;
			width: auto;
			line-height: 1;
			padding-left: 8px;
			border-left-width: 2px;
			border-color: #e2e8f0;
		}
	}
}

.contributor-profile-avatar {
	height: 24px;
	width: 24px;
	border-radius: 24px;
	overflow: hidden;
}
</style>
